import { useEffect, useState, useRef, useCallback } from "react";

interface UseDebouncedValueOptions<T> {
  /** Called with the debounced value each time the debounce fires */
  onDebounce?: (value: T) => void;
}

/**
 * Debounces a value, delaying updates until a specified time has passed without changes.
 *
 * This hook is useful for optimizing performance by reducing the frequency of expensive
 * operations (like API calls or complex calculations) that depend on rapidly changing values.
 *
 * @template T - The type of the value being debounced
 * @param val - The value to debounce
 * @param [ms=300] - The debounce delay in milliseconds (default: 300ms)
 * @param [options] - Optional configuration
 * @param [options.onDebounce] - Callback invoked with the debounced value each time the debounce fires
 * @returns A tuple containing:
 *   - The debounced value (updates after `ms` milliseconds of no changes)
 *   - A cancel function to immediately clear any pending debounce timeout
 *
 * @example
 * ```typescript
 * // Debounce search input to reduce API calls
 * const [searchTerm, setSearchTerm] = useState("");
 * const [debouncedSearch, cancelSearch] = useDebouncedValue(searchTerm, 500, {
 *   onDebounce: (value) => fetchSearchResults(value),
 * });
 *
 * // Cancel pending debounce when component unmounts or user clears input
 * const handleClear = () => {
 *   setSearchTerm("");
 *   cancelSearch();
 * };
 * ```
 *
 * @example
 * ```typescript
 * // Debounce window resize for performance
 * const [windowWidth, setWindowWidth] = useState(window.innerWidth);
 * const [debouncedWidth] = useDebouncedValue(windowWidth, 200);
 *
 * useEffect(() => {
 *   const handleResize = () => setWindowWidth(window.innerWidth);
 *   window.addEventListener('resize', handleResize);
 *   return () => window.removeEventListener('resize', handleResize);
 * }, []);
 * ```
 */
export const useDebouncedValue = <T>(
  val: T,
  ms: number = 300,
  options?: UseDebouncedValueOptions<T>
): [T, () => void] => {
  const [debouncedValue, setDebouncedValue] = useState<T>(val);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onDebounceRef = useRef(options?.onDebounce);
  onDebounceRef.current = options?.onDebounce;
  const lastDebouncedRef = useRef(val);

  const cancelDebounce = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    // Only debounce a genuine change. On mount — and on any remount, including
    // StrictMode's mount/unmount/remount — `val` still equals the last debounced
    // value, so there is nothing to fire. Firing here would write the initial
    // value into external state (e.g. URL search params) before the user has
    // interacted, which can clobber a navigation that happened in between.
    if (Object.is(val, lastDebouncedRef.current)) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      lastDebouncedRef.current = val;
      setDebouncedValue(val);
      onDebounceRef.current?.(val);
    }, ms);

    return () => {
      cancelDebounce();
    };
  }, [val, ms, cancelDebounce]);

  return [debouncedValue, cancelDebounce];
};
